import React, { useState, useEffect } from "react";
import { globalHistory, navigate as reachNavigate } from "@reach/router";
import { styled } from "@material-ui/styles";
import HomeIcon from "@material-ui/icons/Home";
import PeopleIcon from "@material-ui/icons/People";
import PersonIcon from "@material-ui/icons/Person";
import SportsIcon from "@material-ui/icons/Sports";
import EventIcon from "@material-ui/icons/Event";
import { useHasPermission } from "../../hooks/useHasPermission";
import { Permission } from "../auth-context-provider";
import { IStyledArguments } from "../../types/styled-arguments";

/*
 * Nav inferior plano. Se probó un FAB central de acento para "nuevo
 * partido" pero se retiró: cada pantalla (partidos, drills...) ya tiene su
 * propio "+" contextual, así que el del nav quedaba duplicado o sin
 * sentido fuera de la pantalla de partidos. Construido a mano en vez de
 * BottomNavigation/BottomNavigationAction de MUI por herencia de esa
 * prueba — se puede volver al componente de MUI sin problema.
 */

const NavBar = styled("div")(({ theme }: IStyledArguments) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1300,
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
  background: "rgba(255, 255, 255, 0.88)",
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  borderTop: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 -1px 8px rgba(16, 21, 26, 0.06)",
}));

const NavRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-around",
  height: 60,
});

const NavButton = styled("button")<{ $active?: boolean }>(
  ({ theme, $active }: IStyledArguments & { $active?: boolean }) => ({
    appearance: "none",
    background: $active ? theme.palette.extra.accentSoft : "transparent",
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 56,
    padding: "6px 12px",
    margin: "6px 4px",
    borderRadius: 12,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "& svg": {
      fontSize: 22,
      color: $active ? theme.palette.primary.main : theme.palette.text.secondary,
      transform: $active ? "scale(1.1)" : "none",
      transition: "all 0.2s ease",
    },
  })
);

const NavLabel = styled("span")<{ $active?: boolean }>(
  ({ theme, $active }: IStyledArguments & { $active?: boolean }) => ({
    fontSize: 10,
    marginTop: 2,
    fontWeight: $active ? 600 : 500,
    color: $active ? theme.palette.primary.dark : theme.palette.text.secondary,
  })
);

const BottomNavigationMUI: React.FC = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const isCoach = useHasPermission(Permission.LEADERS_ADD);

  useEffect(() => {
    return globalHistory.listen(({ location }) => {
      setCurrentPath(location.pathname);
    });
  }, []);

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return currentPath === "/dashboard" || currentPath === "/";
    }
    return currentPath.startsWith(path);
  };

  const secondaryItem = isCoach
    ? { label: "Coach", path: "/library/drills", icon: <SportsIcon /> }
    : { label: "Players", path: "/people/players", icon: <PeopleIcon /> };

  return (
    <NavBar>
      <NavRow>
        <NavButton
          type="button"
          $active={isActive("/dashboard")}
          aria-current={isActive("/dashboard") ? "page" : undefined}
          onClick={() => reachNavigate("/dashboard")}
        >
          <HomeIcon />
          <NavLabel $active={isActive("/dashboard")}>Home</NavLabel>
        </NavButton>

        <NavButton
          type="button"
          $active={isActive("/people/matches")}
          aria-current={isActive("/people/matches") ? "page" : undefined}
          onClick={() => reachNavigate("/people/matches")}
        >
          <EventIcon />
          <NavLabel $active={isActive("/people/matches")}>Matches</NavLabel>
        </NavButton>

        <NavButton
          type="button"
          $active={isActive(secondaryItem.path)}
          aria-current={isActive(secondaryItem.path) ? "page" : undefined}
          onClick={() => reachNavigate(secondaryItem.path)}
        >
          {secondaryItem.icon}
          <NavLabel $active={isActive(secondaryItem.path)}>
            {secondaryItem.label}
          </NavLabel>
        </NavButton>

        <NavButton
          type="button"
          $active={isActive("/people/teams")}
          aria-current={isActive("/people/teams") ? "page" : undefined}
          onClick={() => reachNavigate("/people/teams")}
        >
          <PeopleIcon />
          <NavLabel $active={isActive("/people/teams")}>Teams</NavLabel>
        </NavButton>

        <NavButton
          type="button"
          $active={isActive("/profile")}
          aria-current={isActive("/profile") ? "page" : undefined}
          onClick={() => reachNavigate("/profile")}
        >
          <PersonIcon />
          <NavLabel $active={isActive("/profile")}>Profile</NavLabel>
        </NavButton>
      </NavRow>
    </NavBar>
  );
};

export default BottomNavigationMUI;
